package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.Item
import co.uk.sacursoftware.ssinventory.data.model.OrderItem
import co.uk.sacursoftware.ssinventory.data.model.Supplier
import co.uk.sacursoftware.ssinventory.data.model.formatStockAmount
import co.uk.sacursoftware.ssinventory.data.model.formatWhole

@Composable
fun OrderExportDialog(
    supplier: Supplier,
    items: List<Item>,
    itemNames: Map<String, String>,
    itemStocks: Map<String, Double>,
    orderItems: List<OrderItem>,
    daysCalculated: String = "7",
    onDismiss: () -> Unit
) {
    val clipboardManager = LocalClipboardManager.current
    var copiedMessage by remember { mutableStateOf<String?>(null) }
    
    val supplierName = supplier.name
    val itemsText = orderItems.joinToString("\n") { item ->
        val qtyDisplay = formatWhole(item.qtyOrdered)
        val name = itemNames[item.itemId] ?: item.name ?: "Item"
        "- $name: $qtyDisplay"
    }

    val whatsappText = buildString {
        appendLine("ORDER FOR BRADFORD - $supplierName")
        append(itemsText)
    }.trim()

    val emailText = buildString {
        appendLine("Hello,")
        appendLine("Please place order for Bradford ($supplierName):")
        appendLine()
        appendLine(itemsText)
        appendLine()
        appendLine("Thank you,")
        append("Boo Bradford.")
    }

    val printCheckText = buildString {
        appendLine("$supplierName order for $daysCalculated days")
        appendLine()
        orderItems.forEach { item ->
            val name = itemNames[item.itemId] ?: item.name ?: "Item #${item.itemId}"
            val countVal = formatWhole(itemStocks[item.itemId] ?: 0.0)
            val predictedVal = formatWhole(item.qtySuggested)
            val orderVal = formatWhole(item.qtyOrdered)
            appendLine(name)
            appendLine("Count: $countVal")
            appendLine("Predicted: $predictedVal")
            appendLine("Order: $orderVal")
            appendLine()
        }
    }.trim()

    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text("Order Summary", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.titleMedium)
                TextButton(onClick = onDismiss, contentPadding = PaddingValues(0.dp)) {
                    Text("DONE", fontWeight = FontWeight.Bold)
                }
            }
        },
        text = {
            Column(modifier = Modifier.fillMaxWidth().verticalScroll(rememberScrollState())) {
                Text(
                    text = supplierName,
                    style = MaterialTheme.typography.titleSmall,
                    color = MaterialTheme.colorScheme.secondary,
                    fontWeight = FontWeight.Bold
                )
                
                Spacer(modifier = Modifier.height(8.dp))
                
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
                    shape = MaterialTheme.shapes.medium
                ) {
                    Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        orderItems.forEachIndexed { index, item ->
                            val name = itemNames[item.itemId] ?: item.name ?: "Item #${item.itemId}"
                            val countVal = formatWhole(itemStocks[item.itemId] ?: 0.0)
                            val predictedVal = formatWhole(item.qtySuggested)
                            val orderVal = formatWhole(item.qtyOrdered)

                            Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
                                Text(name, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodyMedium)
                                Text("Count: $countVal", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.outline)
                                Text("Predicted: $predictedVal", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.outline)
                                Text("Order: $orderVal", style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary)
                            }
                            if (index < orderItems.size - 1) {
                                HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.2f))
                            }
                        }
                    }
                }
                
                if (copiedMessage != null) {
                    Spacer(modifier = Modifier.height(12.dp))
                    Surface(
                        color = MaterialTheme.colorScheme.primaryContainer,
                        shape = MaterialTheme.shapes.small,
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(
                            text = copiedMessage!!,
                            modifier = Modifier.padding(8.dp),
                            style = MaterialTheme.typography.bodySmall,
                            fontWeight = FontWeight.Medium,
                            color = MaterialTheme.colorScheme.onPrimaryContainer
                        )
                    }
                }
            }
        },
        confirmButton = {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    Button(
                        onClick = {
                            clipboardManager.setText(AnnotatedString(whatsappText))
                            copiedMessage = "Text copied to clipboard!"
                        },
                        modifier = Modifier.weight(1f),
                        shape = MaterialTheme.shapes.small,
                        colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.primary),
                        contentPadding = PaddingValues(horizontal = 2.dp, vertical = 6.dp)
                    ) {
                        Text("TEXT", style = MaterialTheme.typography.labelSmall)
                    }

                    Button(
                        onClick = {
                            clipboardManager.setText(AnnotatedString(emailText))
                            copiedMessage = "Email copied to clipboard!"
                        },
                        modifier = Modifier.weight(1f),
                        shape = MaterialTheme.shapes.small,
                        colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.secondary),
                        contentPadding = PaddingValues(horizontal = 2.dp, vertical = 6.dp)
                    ) {
                        Text("EMAIL", style = MaterialTheme.typography.labelSmall)
                    }

                    Button(
                        onClick = {
                            clipboardManager.setText(AnnotatedString(printCheckText))
                            copiedMessage = "Print Check copied to clipboard!"
                        },
                        modifier = Modifier.weight(1f),
                        shape = MaterialTheme.shapes.small,
                        colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.tertiaryContainer),
                        contentPadding = PaddingValues(horizontal = 2.dp, vertical = 6.dp)
                    ) {
                        Text("PRINT", style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.onTertiaryContainer)
                    }
                }
            }
        },
        shape = MaterialTheme.shapes.large
    )
}

@Composable
fun StockCountPrintDialog(
    supplierName: String,
    items: List<Item>,
    previousCounts: Map<String, Double>,
    deliveredQuantities: Map<String, Double>,
    newCounts: Map<String, Double>,
    onDismiss: () -> Unit
) {
    val clipboardManager = LocalClipboardManager.current
    var copiedMessage by remember { mutableStateOf<String?>(null) }

    val printText = buildString {
        appendLine("$supplierName stock count")
        appendLine()
        items.forEach { item ->
            val prev = previousCounts[item.id] ?: 0.0
            val received = deliveredQuantities[item.id] ?: 0.0
            val newAmt = newCounts[item.id] ?: 0.0
            appendLine(item.name)
            appendLine("Previous: ${formatStockAmount(item, prev)}")
            appendLine("Received: ${formatStockAmount(item, received)}")
            appendLine("New count: ${formatStockAmount(item, newAmt)}")
            appendLine()
        }
    }.trim()

    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text("Count Summary", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.titleMedium)
                TextButton(onClick = onDismiss, contentPadding = PaddingValues(0.dp)) {
                    Text("DONE", fontWeight = FontWeight.Bold)
                }
            }
        },
        text = {
            Column(modifier = Modifier.fillMaxWidth().verticalScroll(rememberScrollState())) {
                Text(
                    text = supplierName,
                    style = MaterialTheme.typography.titleSmall,
                    color = MaterialTheme.colorScheme.secondary,
                    fontWeight = FontWeight.Bold
                )
                Spacer(modifier = Modifier.height(8.dp))
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
                    shape = MaterialTheme.shapes.medium
                ) {
                    Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        items.forEachIndexed { index, item ->
                            val prev = previousCounts[item.id] ?: 0.0
                            val received = deliveredQuantities[item.id] ?: 0.0
                            val newAmt = newCounts[item.id] ?: 0.0
                            Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
                                Text(item.name, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodyMedium)
                                Text("Previous: ${formatStockAmount(item, prev)}", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.outline)
                                Text("Received: ${formatStockAmount(item, received)}", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.outline)
                                Text("New count: ${formatStockAmount(item, newAmt)}", style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary)
                            }
                            if (index < items.size - 1) {
                                HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.2f))
                            }
                        }
                    }
                }
                if (copiedMessage != null) {
                    Spacer(modifier = Modifier.height(12.dp))
                    Surface(
                        color = MaterialTheme.colorScheme.primaryContainer,
                        shape = MaterialTheme.shapes.small,
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(
                            text = copiedMessage!!,
                            modifier = Modifier.padding(8.dp),
                            style = MaterialTheme.typography.bodySmall,
                            fontWeight = FontWeight.Medium,
                            color = MaterialTheme.colorScheme.onPrimaryContainer
                        )
                    }
                }
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    clipboardManager.setText(AnnotatedString(printText))
                    copiedMessage = "Count copied to clipboard!"
                },
                modifier = Modifier.fillMaxWidth(),
                shape = MaterialTheme.shapes.small
            ) {
                Text("PRINT")
            }
        },
        shape = MaterialTheme.shapes.large
    )
}
